import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

/**
 * Haptic feedback utilities for Access Gate
 * Provides consistent haptic patterns across the app
 */

// Check if haptics are available (native app only)
const isNative = () => {
  return window.Capacitor?.isNativePlatform ?? false;
};

/**
 * Trigger a haptic impact
 * @param {string} style - 'light' | 'medium' | 'heavy'
 */
export const triggerImpact = async (style = 'medium') => {
  if (!isNative()) return;

  const impactStyles = {
    light: ImpactStyle.Light,
    medium: ImpactStyle.Medium,
    heavy: ImpactStyle.Heavy,
  };

  try {
    await Haptics.impact({ style: impactStyles[style] ?? ImpactStyle.Medium });
  } catch (error) {
    console.warn('[Haptics] Impact failed:', error.message);
  }
};

/**
 * Trigger a haptic notification
 * @param {string} type - 'success' | 'warning' | 'error'
 */
export const triggerNotification = async (type = 'success') => {
  if (!isNative()) return;

  const notificationTypes = {
    success: NotificationType.Success,
    warning: NotificationType.Warning,
    error: NotificationType.Error,
  };

  try {
    await Haptics.notification({
      type: notificationTypes[type] ?? NotificationType.Success,
    });
  } catch (error) {
    console.warn('[Haptics] Notification failed:', error.message);
  }
};

/**
 * Vibrate for a specific duration
 * @param {number} duration - Duration in milliseconds
 */
export const vibrate = async (duration = 10) => {
  if (!isNative()) return;

  // Fallback to navigator.vibrate web API
  if (navigator.vibrate) {
    navigator.vibrate(duration);
    return;
  }

  try {
    await Haptics.vibrate({ duration });
  } catch (error) {
    console.warn('[Haptics] Vibrate failed:', error.message);
  }
};

/**
 * Haptic patterns for Access Gate
 */
export const AccessGateHaptics = {
  /**
   * Haptic feedback when shake is detected
   */
  shakeDetected: () => triggerImpact('medium'),

  /**
   * Haptic feedback when PIN digit is entered
   */
  pinEntered: () => triggerImpact('light'),

  /**
   * Haptic feedback for correct PIN
   */
  pinCorrect: () => triggerNotification('success'),

  /**
   * Haptic feedback for wrong PIN
   */
  pinWrong: async () => {
    // Double tap for error
    await triggerImpact('heavy');
    setTimeout(() => triggerImpact('heavy'), 100);
  },
};

export default AccessGateHaptics;
